import { useQuery } from '@tanstack/react-query';
import { endpoints } from 'api/endpoints';
import { isExistingSiteEntryMode } from 'config/entry-mode';
import { useMemo } from 'react';
import { useAppStore } from 'stores/appStore';
import type { GetSiteImagesHandlerResponse, SelectableImage, SiteImagesSource } from 'types/site-images';
import { resolveImagesSource, resolveSelectableImages } from 'utils/site-images';

const SITE_IMAGES_LIMIT = 8;

export function useSiteImages() {
  const entryMode = useAppStore((state) => state.entryMode);
  const metaSiteId = useAppStore((state) => state.metaSiteId);

  const isExistingSiteMode = isExistingSiteEntryMode(entryMode);

  const siteImagesQuery = useQuery({
    queryKey: ['site-images', metaSiteId],
    queryFn: () => endpoints.getSiteImages({ limit: SITE_IMAGES_LIMIT }),
    enabled: isExistingSiteMode && Boolean(metaSiteId),
    staleTime: Infinity,
  });

  const { data, isPending, isSuccess, isError } = siteImagesQuery;

  const images = useMemo((): SelectableImage[] => {
    return resolveSelectableImages(isExistingSiteMode, data, isError);
  }, [isExistingSiteMode, data, isError]);

  const isLoading = isExistingSiteMode && isPending;
  const isReady = !isExistingSiteMode || isSuccess || isError;

  const imagesSource: SiteImagesSource | 'static' | undefined = useMemo(() => {
    return resolveImagesSource(isExistingSiteMode, data, isError);
  }, [isExistingSiteMode, data, isError]);

  return {
    images,
    isLoading,
    isReady,
    imagesSource,
    siteImages: data as GetSiteImagesHandlerResponse | undefined,
  };
}
